import {
  statusMapParser,
  statusValues,
} from '@bsa/scouting-js-utils/src/constants/advancements';
import { createSelector } from '@reduxjs/toolkit';
import {
  chain,
  filter,
  find,
  get,
  groupBy,
  indexOf,
  isEmpty,
  isNil,
  map,
  orderBy,
  reduce,
  reverse,
  set,
  sortBy,
  toLower,
  uniqBy,
} from 'lodash';
import moment from 'moment';
import stable from 'stable';

import { isScoutsBsaUnitSel, programSel } from '@context/duck/selectorsTyped';
import { coreAwardsSel } from '@dict/duck/selectors';
import { payloadSel, querySel } from '@location';
import { awardsByProgram } from '@modules/advancement/constants';
import { currentUserSubunitsSel } from '@modules/advancement/packRoster/duck/selectors';
import { advancementHistoryItemsSel } from '@modules/advancement/pendingItems/duck/selectorsTyped';
import {
  advancementStatus,
  canApproveAdvancement,
  getUnapproveConstraint,
  isValueTruthy,
} from '@modules/shared/utils';
import {
  ProgramId,
  advancementStatuses,
  advancementTypes,
  advancementsExpiredKeys,
  emptyObj,
  historyItemsTypes,
  programForUnitTypeId,
  rankName,
  ranksIds,
  venturingCoreAwardsIds,
} from '@shared/constants';
import {
  adventuresSel as adventureDictSel,
  awardsSel as awardDictSel,
  hasNewPermissionSel,
  isParentGuardianSel,
  isParentOrYouthMemberSel,
  isYouthMemberSel,
  meritBadgesSel as meritBadgeDictSel,
  organizationGuidSel,
  organizationPositionSel,
  packRosterItemsSel,
  ranksSel as rankDictSel,
  selectedOrganizationSel,
  ssElectivesSel as ssElectivesDictSel,
  unitTypeIdSel,
  urlUserIdSel,
} from '@shared/duck/selectors/general.selectors';
import { newPermissions } from '@shared/newPermissions';
import {
  canViewRosterSel,
  profileSel as selfProfileSel,
  userIdSel as selfUserIdSel,
} from '@user/duck/selectors';
import {
  canEditAdvancement,
  makeSearch,
  personNameBuilder,
  sorters,
} from '@utils';

import { normalizeRankData } from '../../advancement/packRoster/utils';
import { childrenSel } from '../../parentGuardian/duck/selectors';
import {
  NewAdvancementFilter,
  advancementFilters,
  advancementSorters,
  advancementsStates,
  moduleName,
  sortAdvancementTypes,
} from '../constants';
import {
  getAdvancementUid,
  hasDenChiefPositionInAnyUnitAndIsPackSelected,
} from '../utils';

const youthProfileModuleSel = state => state[moduleName];

export const isMemberDetailsLoadingSel = state =>
  youthProfileModuleSel(state).isMemberDetailsLoading;
export const isYouthAdvancementsLoadingSel = state =>
  youthProfileModuleSel(state).isYouthAdvancementsLoading;
export const isEditingSel = state => youthProfileModuleSel(state).isEditing;

export const userIdSel = createSelector(
  urlUserIdSel,
  selfUserIdSel,
  isYouthMemberSel,
  (urlUserId, selfUserId, isYouthMember) =>
    isYouthMember ? selfUserId : urlUserId,
);

export const activityTypeSel = state => payloadSel(state).activityType;

export const isYouthWithinUnitSel = createSelector(
  userIdSel,
  packRosterItemsSel,
  (userId, packRoster) => !!packRoster.find(item => item.userId == userId),
);

export const memberDetailsSel = state =>
  youthProfileModuleSel(state).memberDetails || emptyObj;

const groupAndSortRanks = ranks =>
  chain(ranks)
    .groupBy('programId')
    .mapValues(programRanks => orderBy(programRanks, 'level', 'desc'))
    .toPairs()
    .orderBy(([key]) => Number(key), 'desc')
    .reduce(
      (curr, [rankProgramKey, sortedProgramRanks]) => ({
        ...curr,
        [programForUnitTypeId[rankProgramKey]]: sortedProgramRanks,
      }),
      {},
    )
    .value();

export const highestRankByDetailsSel = createSelector(
  memberDetailsSel,
  isScoutsBsaUnitSel,
  (memberDetails, isScoutsBsaUnit) => {
    const ignoredRanks = isScoutsBsaUnit
      ? []
      : [ranksIds.SCOUT, ranksIds.TENDERFOOT, ranksIds.SECOND_CLASS];

    const approvedRanks =
      memberDetails.ranks?.filter(
        rank =>
          [advancementStatuses.APPROVED, advancementStatuses.AWARDED].includes(
            rank.status,
          ) && !ignoredRanks.includes(rank.id),
      ) || [];
    const highestRanks = groupAndSortRanks(approvedRanks);
    return highestRanks ?? {};
  },
);

export const includeScoutsBSAJoinedDateSel = createSelector(
  memberDetailsSel,
  isScoutsBsaUnitSel,
  (memberDetails, isScoutsBsaUnit) => {
    if (isScoutsBsaUnit) return true;

    const isWorkingOnBSAAdvancement = memberDetails?.ranks?.some(
      rank =>
        [advancementStatuses.APPROVED, advancementStatuses.AWARDED].includes(
          rank.status,
        ) && rank.name === rankName.FIRST_CLASS,
    );

    return isWorkingOnBSAAdvancement;
  },
);

export const memberDetailsLoadedSel = createSelector(
  memberDetailsSel,
  memberDetails => !isEmpty(memberDetails),
);

export const youthDetailsSaveLoadingSel = state =>
  youthProfileModuleSel(state).youthDetailsSaveLoading;

export const youthProfilePictureSaveLoadingSel = state =>
  youthProfileModuleSel(state).youthProfilePictureSaveLoading;

export const memberEagleDetailsSel = createSelector(
  memberDetailsSel,
  memberDetails =>
    !isEmpty(memberDetails.profile)
      ? Object.fromEntries(
          Object.entries(memberDetails.profile).filter(([key]) =>
            key.includes('eagle'),
          ),
        )
      : {},
);

export const shouldMemberDetailsReloadSel = state =>
  youthProfileModuleSel(state).shouldMemberDetailsReload;

export const setEditYouthDetailSidebarSel = state =>
  youthProfileModuleSel(state).setEditYouthDetailSidebar;

export const isEagleActivityLoadingSel = state =>
  youthProfileModuleSel(state).isEagleActivityLoading;

/** @deprecated use hook `useYouthEagleActivity` instead */
export const eagleActivitySel = state =>
  youthProfileModuleSel(state).eagleActivity;

const rosterYouthProfileInfoSel = createSelector(
  userIdSel,
  packRosterItemsSel,
  (selectedUserId, packRoster) => ({
    ...(packRoster.find(({ userId }) => userId == selectedUserId) || {}),
    userId: selectedUserId,
  }),
);

/**
 * @returns {import('@modules/advancement/duck/types').RosterScout}
 */
const convertToYouthProfileModel = ({ userId, profile, memberDetails }) => {
  const { pictureUrl = '', dateOfBirth } = profile;
  let rankData = {};
  if (!isEmpty(memberDetails)) {
    const { ranks = [] } = memberDetails;
    const normalizedRanks = ranks.map(normalizeRankData);

    const sortedRanks = normalizedRanks.sort(
      (rankA, rankB) => rankB.level - rankA.level,
    );

    const highestRankApproved = sortedRanks.filter(
      rank => rank.status === advancementStatuses.APPROVED,
    );

    const highestRanksAwarded = sortedRanks.filter(
      rank => rank.status === advancementStatuses.AWARDED,
    );

    rankData = {
      highestRankApproved,
      highestRanksAwarded,
      currentHighestRankApproved: sortedRanks.find(
        rank => rank.status === 'approved' || rank.status === 'awarded',
      ),
    };
  }

  const youth = {
    ...rankData,
    ...profile,
    userId,
    personShortFullName: personNameBuilder.short(profile),
    isAdult: false,
    pictureUrl,
    age: moment().diff(moment(dateOfBirth), 'years'),
  };

  return youth;
};

export const getYouthCubScoutProgramSel = createSelector(
  memberDetailsSel,
  organizationGuidSel,
  (memberDetails, organizationGuid) => {
    const { currentProgramsAndRanks = [], organizationPositions = [] } =
      memberDetails;
    const organizationPosition = organizationPositions.find(
      position => position.organizationGuid === organizationGuid,
    );
    const unitId = organizationPosition?.organizationId;
    let cubScoutProgram;
    if (Array.isArray(currentProgramsAndRanks)) {
      cubScoutProgram = currentProgramsAndRanks.find(
        program =>
          unitId === program.unitId &&
          +program.programId === ProgramId.CUB_SCOUT,
      );
    } else if (
      typeof currentProgramsAndRanks === 'object' &&
      unitId === currentProgramsAndRanks.unitId &&
      +currentProgramsAndRanks.programId === ProgramId.CUB_SCOUT
    ) {
      cubScoutProgram = currentProgramsAndRanks;
    }
    return cubScoutProgram;
  },
);

export const getYouthCurrentDen = createSelector(
  getYouthCubScoutProgramSel,
  cubScoutProgram => (cubScoutProgram ? cubScoutProgram.denType : null),
);

export const getYouthCurrentDenRankId = createSelector(
  getYouthCubScoutProgramSel,
  rankDictSel,
  (cubScoutProgram, ranksDict) => {
    if (cubScoutProgram?.denType) {
      const rankId = get(
        find(
          ranksDict,
          rank => toLower(rank.name) === toLower(cubScoutProgram.denType),
        ),
        'id',
      );

      return rankId;
    }
    return null;
  },
);

export const getYouthCurrentDenRankLevel = createSelector(
  getYouthCubScoutProgramSel,
  rankDictSel,
  (cubScoutProgram, ranksDict) => {
    if (cubScoutProgram?.denType) {
      const rankFound = find(
        ranksDict,
        rank => toLower(rank.name) === toLower(cubScoutProgram.denType),
      );
      const rankLevel = rankFound?.level;
      return rankLevel;
    }
    return null;
  },
);

const mergeAdvancements = (youthAdvancements, dict, advancementType) => {
  const merged = [...youthAdvancements];
  dict.forEach(item => {
    if (!merged.find(mergedItem => mergedItem.id == item.id)) {
      merged.push({
        ...item,
        advancementType,
      });
    }
    merged.push({
      ...item,
      status: advancementStatuses.DICTIONARY,
      advancementType,
    });
  });

  return merged;
};

const enrichedAdventuresSel = createSelector(
  memberDetailsSel,
  adventureDictSel,
  (memberDetails, adventureDict) => {
    if (isEmpty(memberDetails)) {
      return [];
    }
    const { adventures } = memberDetails;
    const mergedAdventures = mergeAdvancements(
      adventures,
      adventureDict,
      advancementTypes.ADVENTURES,
    );
    return mergedAdventures;
  },
);

export const allAdventuresByIdSel = createSelector(
  enrichedAdventuresSel,
  enrichedAdventures => groupBy(enrichedAdventures, 'adventureId'),
);

export const adventuresSel = createSelector(
  enrichedAdventuresSel,
  enrichedAdventures => {
    // Filter expired adventures
    const filtered = filter(enrichedAdventures, adventure =>
      isNil(adventure.expiryDt)
        ? true
        : !moment().isAfter(moment(adventure.expiryDt)) ||
          (statusValues[statusMapParser[adventure.status]] ||
            statusValues.NOT_STARTED) >= statusValues.COMPLETED,
    );
    return filtered;
  },
);

export const pastYouthApprovedAdventuresSel = createSelector(
  adventuresSel,
  getYouthCurrentDenRankId,
  (adventures, denRankId) =>
    reverse(
      sortBy(
        filter(
          adventures,
          ({ status, rankId }) =>
            statusValues[statusMapParser[status]] > statusValues.COMPLETED &&
            (!denRankId || Number(denRankId) !== Number(rankId)),
        ),
        ({ dateCompleted }) => dateCompleted,
      ),
    ),
);

const filterAwardByProgram = (award, program) =>
  awardsByProgram[program]?.has(Number(award.id));

const filterByStatus = (filter, advancement) => {
  const { status } = advancement || {};
  switch (filter) {
    case advancementFilters.ALL: {
      return status !== advancementStatuses.DICTIONARY;
    }
    case advancementFilters.ALL_SSELECTIVES: {
      return status === advancementStatuses.DICTIONARY;
    }
    case advancementFilters.APPROVED: {
      return (
        status === advancementStatuses.APPROVED ||
        status === advancementStatuses.AWARDED
      );
    }
    case NewAdvancementFilter.APPROVED_COMPLETED: {
      return (
        status === advancementStatuses.APPROVED ||
        status === advancementStatuses.AWARDED ||
        status === advancementStatuses.COMPLETED ||
        status === advancementStatuses.COUNSELOR_APPROVED
      );
    }
    case advancementFilters.STARTED: {
      return status === advancementStatuses.STARTED;
    }
    case advancementFilters.NOT_STARTED: {
      return !status;
    }
    case advancementFilters.COMPLETED: {
      return (
        status === advancementStatuses.COMPLETED ||
        status === advancementStatuses.COUNSELOR_APPROVED
      );
    }
    default: {
      return status !== advancementStatuses.DICTIONARY;
    }
  }
};

const filterAdvancements = (
  advancements,
  unitTypeId,
  hasMinFirstClass,
  currentProgram,
) =>
  advancements.filter(advancement => {
    const {
      unitTypeId: itemUnitTypeId,
      programId,
      advancementType: itemAdvancementType,
    } = advancement;
    // Check award program filter
    if (itemAdvancementType === advancementTypes.AWARDS) {
      return (
        filterAwardByProgram(advancement, currentProgram) ||
        !filterByStatus(advancementFilters.NOT_STARTED, advancement)
      );
    } else if (
      [ProgramId.VENTURING, ProgramId.SEA_SCOUT].includes(unitTypeId) &&
      !hasMinFirstClass
    ) {
      return !(
        itemUnitTypeId == ProgramId.BOY_SCOUT ||
        programId == ProgramId.BOY_SCOUT
      );
    } else
      return (
        isNil(itemUnitTypeId) ||
        isEmpty(itemUnitTypeId) ||
        +unitTypeId === +itemUnitTypeId
      );
  });

export const highestCoreAwardSel = createSelector(
  memberDetailsSel,
  extraInfo => {
    const recordedAwards = extraInfo.awards || [];
    const recordedCoreAwards = recordedAwards.filter(
      award =>
        venturingCoreAwardsIds.includes(award.awardId) &&
        advancementStatus.isAtLeastApproved(award),
    );
    return recordedCoreAwards.pop();
  },
);

const getCoreAwardState = (award, highestCoreAward = {}) =>
  Number(highestCoreAward.awardId) > Number(award.awardId)
    ? advancementsStates.PREVIOUS
    : Number(highestCoreAward.awardId) == Number(award.awardId)
    ? advancementsStates.LAST_APPROVED
    : advancementsStates.NEXT;

export const venturingCoreAwardsSel = createSelector(
  memberDetailsSel,
  coreAwardsSel,
  highestCoreAwardSel,
  (memberDetails, coreAwards, highestCoreAward) => {
    const recordedAwards = memberDetails.awards || [];
    const mergedAwards = coreAwards.map(award => {
      const recordedAward = recordedAwards.find(
        ({ awardId }) => award.id == awardId,
      );
      if (recordedAward) {
        return recordedAward;
      }
      return {
        ...award,
        uid: getAdvancementUid(award, advancementTypes.AWARDS),
      };
    });
    return mergedAwards.map(award => ({
      ...award,
      state: getCoreAwardState(award, highestCoreAward),
    }));
  },
);

const unitRanksSel = createSelector(
  rankDictSel,
  unitTypeIdSel,
  (_state, unitTypeId) => unitTypeId,
  (ranks, unitTypeId, customUnitTypeId) => {
    const selectedProgramId = customUnitTypeId || unitTypeId;
    return ranks.filter(({ programId }) => selectedProgramId == programId);
  },
);

const ownChildrenSelV2 = createSelector(
  urlUserIdSel,
  childrenSel,
  memberDetailsSel,
  (urlUserId, children, memberDetails) => {
    const ownChild = children.find(({ userId }) => userId === urlUserId);
    if (ownChild) {
      return convertToYouthProfileModel({
        memberDetails,
        userId: urlUserId,
        profile: { ...ownChild, ...memberDetails },
      });
    }
    return false;
  },
);

const selfYouthProfileSel = createSelector(
  selfUserIdSel,
  selfProfileSel,
  memberDetailsSel,
  (selfUserId, selfProfile, memberDetails) =>
    convertToYouthProfileModel({
      memberDetails,
      userId: selfUserId,
      profile: selfProfile,
    }),
);

export const youthInfoSel = state => {
  const { adultUserId = '' } = querySel(state) || {};
  const memberDetails = memberDetailsSel(state);
  let youth;
  switch (true) {
    case canViewRosterSel(state): {
      youth = rosterYouthProfileInfoSel(state);
      break;
    }
    case isParentGuardianSel(state): {
      youth = ownChildrenSelV2(state);
      break;
    }
    case !!adultUserId && !isEmpty(memberDetails): {
      const userId = urlUserIdSel(state);
      youth = convertToYouthProfileModel({
        memberDetails,
        userId,
        profile: memberDetails.profile,
      });
      break;
    }
    default:
      youth = selfYouthProfileSel(state);
  }

  const {
    pictureUrl,
    isAdult,
    age,
    personShortFullName,
    currentHighestRankApproved,
    otherHighestRanksApproved,
    hasMinFirstClass,
  } = youth;
  const scoutsBSAHighestRank = (otherHighestRanksApproved || []).find(
    ({ unitTypeId }) => unitTypeId == ProgramId.BOY_SCOUT,
  );

  return {
    pictureUrl,
    isAdult,
    age,
    hasMinFirstClass,
    personShortFullName,
    scoutsBSAHighestRank,
    currentHighestRankApproved,
    userId: youth.userId,
  };
};

export const highestRankSel = state =>
  youthInfoSel(state).currentHighestRankApproved;

export const isAdvancementInYouthRecordSel = createSelector(
  memberDetailsSel,
  payloadSel,
  (memberDetails = {}, routeParams = {}) => {
    const { advancementId, advancementType } = routeParams;
    const advancementInYouthRecord = find(memberDetails[advancementType], {
      id: +advancementId,
    });
    return !isNil(advancementInYouthRecord);
  },
);

const getRankState = (rank, highestRank = {}) =>
  Number(highestRank.level) > Number(rank.level)
    ? advancementsStates.PREVIOUS
    : Number(highestRank.level) == Number(rank.level)
    ? advancementsStates.LAST_APPROVED
    : advancementsStates.NEXT;

const getRanks = ({ ranksList, recordedRanks, highestRank }) =>
  ranksList
    .map(rank => {
      const recordedRank = recordedRanks.find(({ id }) => id == rank.id);
      if (recordedRank) {
        return recordedRank;
      }
      return { ...rank, uid: getAdvancementUid(rank, advancementTypes.RANKS) };
    })
    .map(rank => ({
      ...rank,
      state: getRankState(rank, highestRank),
    }));

export const ranksSel = createSelector(
  highestRankSel,
  memberDetailsSel,
  (state, unitTypeId) => unitRanksSel(state, unitTypeId),
  (highestRank, memberDetails, ranksList) => {
    const recordedRanks = memberDetails.ranks || [];
    const ranks = getRanks({ ranksList, recordedRanks, highestRank });
    const sortedRanks = stable(
      ranks,
      (a, b) => Number(a.level) > Number(b.level),
    );
    return sortedRanks;
  },
);

const highestHierarchicalAdvancementSel = createSelector(
  unitTypeIdSel,
  highestRankSel,
  highestCoreAwardSel,
  (unitTypeId, highestRank, highestCoreAward) =>
    unitTypeId == ProgramId.VENTURING ? highestCoreAward : highestRank,
);

const advancementsSel = createSelector(
  memberDetailsSel,
  unitTypeIdSel,
  programSel,
  youthInfoSel,
  awardDictSel,
  adventureDictSel,
  meritBadgeDictSel,
  rankDictSel,
  ssElectivesDictSel,
  (
    memberDetails,
    unitTypeId,
    currentProgram,
    youthInfo = {},
    awardDict = [],
    adventureDict = [],
    meritBadgeDict = [],
    rankDict = [],
    ssElectivesDict = [],
  ) => {
    if (isEmpty(memberDetails)) {
      return [];
    }

    const {
      awards = [],
      meritBadges = [],
      adventures = [],
      ranks = [],
      ssElectives = [],
    } = memberDetails;
    const advancements = [
      ...mergeAdvancements(awards, awardDict, advancementTypes.AWARDS),
      ...mergeAdvancements(
        meritBadges,
        meritBadgeDict,
        advancementTypes.MERIT_BADGES,
      ),
      ...mergeAdvancements(
        adventures,
        adventureDict,
        advancementTypes.ADVENTURES,
      ),
      ...mergeAdvancements(ranks, rankDict, advancementTypes.RANKS),
      ...mergeAdvancements(
        ssElectives,
        ssElectivesDict,
        advancementTypes.SSELECTIVES,
      ),
    ];
    return filterAdvancements(
      advancements,
      unitTypeId,
      youthInfo.hasMinFirstClass,
      currentProgram,
    );
  },
);

const scoutsBSAHighestRankSel = state =>
  youthInfoSel(state).scoutsBSAHighestRank;

export const startedItemsSel = createSelector(
  advancementsSel,
  highestHierarchicalAdvancementSel,
  unitTypeIdSel,
  scoutsBSAHighestRankSel,
  (
    advancements,
    highestHierarchicalAdvancement,
    unitTypeId,
    scoutsBSAHighestRank,
  ) =>
    advancements
      .filter(({ status }) => status === advancementStatuses.STARTED)
      .map(advancement => ({
        ...advancement,
        isApprovable: canApproveAdvancement({
          unitTypeId,
          advancement,
          scoutsBSAHighestRank,
          highestHierarchicalAdvancement,
        }),
      })),
);

export const approvedItemsSel = createSelector(
  advancementsSel,
  userIdSel,
  programSel,
  packRosterItemsSel,
  (advancements, userId, program, youthItems) =>
    advancements
      .filter(advancement => advancementStatus.isAtLeastApproved(advancement))
      .map(item => {
        const isEditable = canEditAdvancement({
          item,
          program,
          youthItems,
        });
        const isUnapprovable =
          isEditable &&
          !getUnapproveConstraint({
            item: { ...item, userId },
            program,
            advancementsHistory: advancements,
          });

        return {
          ...item,
          userId,
          isEditable,
          isUnapprovable,
        };
      }),
);

export const completedItemsSel = createSelector(
  advancementsSel,
  userIdSel,
  programSel,
  packRosterItemsSel,
  (advancements, userId, program, youthItems) =>
    advancements
      .filter(
        advancement =>
          (advancementStatus.isCompleted(advancement) ||
            advancementStatus.isCounselorApproved(advancement)) &&
          !advancementStatus.isApproved(advancement),
      )
      .map(item => {
        const isEditable = canEditAdvancement({
          item,
          program,
          youthItems,
        });
        const isUnapprovable =
          isEditable &&
          !getUnapproveConstraint({
            item: { ...item, userId },
            program,
            advancementsHistory: advancements,
          });

        return {
          ...item,
          userId,
          isEditable,
          isUnapprovable,
        };
      }),
);

function getDate(date) {
  return date === '' || !date ? '9999-99-99' : date;
}

export const seaScoutsItemsSel = createSelector(
  advancementsSel,
  advancements => {
    const seaElectives = sortBy(
      filter(
        advancements,
        item => item.advancementType === advancementTypes.SSELECTIVES,
      ),
      item =>
        `${getDate(item.leaderApprovedDate)}-${item.ssElectiveLevel}-${getDate(
          item.dateCompleted,
        )}-${item.ssElectiveLevel}-${getDate(item.date)}-${
          item.ssElectiveLevel
        }`,
    );
    const sortOrder = map(uniqBy(seaElectives, 'ssElectiveId'), item =>
      item.ssElectiveId.toString(),
    );
    return {
      groups: reduce(
        groupBy(seaElectives, 'ssElectiveId'),
        (memo, group, index) => {
          set(memo, indexOf(sortOrder, index), group);
          return memo;
        },
        [],
      ),
      electiveIds: sortOrder,
    };
  },
);

export const youthProfileInfoSel = createSelector(
  youthInfoSel,
  memberDetailsSel,
  (youthInfo, fetchedInfo) => ({ ...youthInfo, ...fetchedInfo }),
);

export const relationshipsSel = createSelector(
  youthProfileInfoSel,
  youthProfileInfo => get(youthProfileInfo, 'relationships', []),
);

export const youthAdvRequirementsParamsSel = state => payloadSel(state);
export const youthAdvRequirementsLoadingSel = state =>
  youthProfileModuleSel(state).youthAdvRequirementsLoading;
export const youthAdvRequirementsSel = createSelector(
  youthProfileModuleSel,
  advancementHistoryItemsSel,
  programSel,
  (youthProfile, itemsHistory, program) => {
    const { youthAdvRequirements, memberDetails } = youthProfile;
    const { userId } = memberDetails || {};
    const unapproveLock = getUnapproveConstraint({
      program,
      item: { ...youthAdvRequirements, userId },
      advancementsHistory: itemsHistory,
    });

    const advancement = {
      canUnapprove: !unapproveLock,
      unapproveLock,
      type: historyItemsTypes.ADVANCEMENT,
      ...youthAdvRequirements,
    };
    return advancement;
  },
);
export const isPalmAwardDetailsScreenSel = createSelector(
  youthAdvRequirementsSel,
  advancement => {
    if (!advancement) return false;
    // matching against name since there are more than 20 palms
    const nameRegex = /eagle palm pin.*#(?<palmNumber>\d+)/i;
    const match = nameRegex.exec(advancement.name);
    if (!match) return false;

    if (match?.groups?.palmNumber) return true;

    // false by default
    return false;
  },
);
export const youthSel = createSelector(
  userIdSel,
  memberDetailsSel,
  (
    userId,
    { profile: currentYouthProfile, userId: currentYouthProfileUserId },
  ) =>
    currentYouthProfile && userId == currentYouthProfileUserId
      ? {
          ...currentYouthProfile,
          personShortFullName: personNameBuilder.short(currentYouthProfile),
        }
      : {},
);
export const youthAdvNotFoundSel = state =>
  youthProfileModuleSel(state).youthAdvNotFound;

export const advListFilterSel = state =>
  youthProfileModuleSel(state).advListFilter;
export const advListSorterSel = state =>
  youthProfileModuleSel(state).advListSorter;
export const advListSearchSel = state =>
  youthProfileModuleSel(state).advListSearch;

const getAdvListSorter = (sorter, advType) => {
  switch (sorter) {
    case advancementSorters.ALPHA: {
      return sorters.text('name');
    }
    case advancementSorters.ALPHA_DESC: {
      return sorters.text('name', true);
    }
    case advancementSorters.DATE_EARNED: {
      return advType === sortAdvancementTypes.MERIT_BADGES ||
        advType === sortAdvancementTypes.ADVENTURES ||
        advType === sortAdvancementTypes.SSELECTIVES
        ? sorters.date('dateCompleted')
        : sorters.date('dateEarned');
    }
    case advancementSorters.DATE_APPROVED: {
      return sorters.date('leaderApprovedDate', true);
    }
    case advancementSorters.DATE_EARNED_DESC: {
      return advType === sortAdvancementTypes.MERIT_BADGES ||
        advType === sortAdvancementTypes.ADVENTURES ||
        advType === sortAdvancementTypes.SSELECTIVES
        ? sorters.date('dateCompleted', true)
        : sorters.date('dateEarned', true);
    }
    default: {
      return sorters.text('name');
    }
  }
};

export const filteredAdvListItemsSel = createSelector(
  advancementsSel,
  payloadSel,
  advListFilterSel,
  advListSorterSel,
  advListSearchSel,
  (advancements, routeParams, filter, sorter, search) => {
    const { advancementType } = routeParams;
    const searchIncludes = makeSearch(search);

    const filteredAdvancements = advancements.filter(item => {
      // Check if rank is active or awarded
      if (item.advancementType === advancementTypes.RANKS) {
        if (!isValueTruthy(item.active) && !item.awarded) {
          return false;
        }
      }

      // Check adventure expiry for non-approved items
      if (
        item.advancementType === advancementTypes.ADVENTURES &&
        !filterByStatus(advancementFilters.APPROVED, item)
      ) {
        const expiryDt = item[advancementsExpiredKeys.ADVENTURES];
        if (expiryDt && !moment(expiryDt).isSameOrAfter(moment())) {
          return false;
        }
      }

      // Check advancement type, search, and status filters
      return (
        item.advancementType === advancementType &&
        searchIncludes(item.name) &&
        filterByStatus(filter, item)
      );
    });

    if (filter === advancementFilters.ALL_SSELECTIVES) {
      filteredAdvancements.forEach(advancement => {
        const { ssElectiveId } = advancement;
        advancement.electiveRequirements = advancements.filter(
          advancement =>
            advancement.advancementType === advancementTypes.SSELECTIVES &&
            advancement.status !== advancementStatuses.DICTIONARY &&
            advancement.ssElectiveId === ssElectiveId,
        );
      });
    }

    return stable(
      filteredAdvancements,
      getAdvListSorter(sorter, advancementType),
    );
  },
);

export const youthPersonGuidSel = createSelector(
  memberDetailsSel,
  isParentOrYouthMemberSel,
  userIdSel,
  packRosterItemsSel,
  (memberDetails, isParentOrYouthMember, userId, rosterItems) => {
    if (memberDetails && isParentOrYouthMember) {
      const { profile: { personGuid: guid } = {} } = memberDetails || {};
      return guid;
    } else if (!isParentOrYouthMember) {
      const foundPerson = rosterItems.find(person => person.userId == userId);
      return foundPerson && foundPerson.personGuid;
    }
  },
);
export const youthPersonIdSel = createSelector(userIdSel, id => id);

export const updatingYouthAdvancementSel = state =>
  youthProfileModuleSel(state).updatingYouthAdvancement;

export const advReqEditModalOpenSel = state =>
  youthProfileModuleSel(state).advReqEditModalOpen;

export const advReqToEditSel = state =>
  youthProfileModuleSel(state).advReqToEdit;

export const activityLogsSummarySel = state =>
  youthProfileModuleSel(state).activityLogsSummary;

export const isLoadingActivityLogsSummarySel = state =>
  youthProfileModuleSel(state).isLoadingActivityLogsSummary;

export const isLoadingSubmitEagleProjectSel = state =>
  youthProfileModuleSel(state).isLoadingSubmitEagleProject;

export const youthPatrolsSel = createSelector(
  memberDetailsSel,
  organizationGuidSel,
  (memberDetails, organizationGuid) => {
    const foundPositions = memberDetails?.organizationPositions?.find(
      orgPos => orgPos.organizationGuid === organizationGuid,
    );
    if (foundPositions) {
      return uniqBy(
        foundPositions.positions.filter(pos => pos.patrolId),
        'patrolId',
      );
    }
    return [];
  },
);

export const patrolIdSelect = createSelector(
  memberDetailsSel,
  organizationGuidSel,
  (memberDetails, organizationGuid) =>
    memberDetails.organizationPositions
      ? memberDetails.organizationPositions
          .find(orgPos => orgPos.organizationGuid === organizationGuid)
          ?.positions.find(pos => pos.patrolId)?.patrolId
      : null,
);

export const denIdSelect = createSelector(
  memberDetailsSel,
  organizationGuidSel,
  (memberDetails, organizationGuid) =>
    memberDetails.organizationPositions
      ? memberDetails.organizationPositions
          .find(orgPos => orgPos.organizationGuid === organizationGuid)
          ?.positions.find(pos => pos.denId)?.denId
      : null,
);

export const isDenChiefInAnyUnitAndIsPackSel = createSelector(
  selectedOrganizationSel,
  memberDetailsSel,
  (selectedOrganization, memberDetails) => {
    const isDenChief = hasDenChiefPositionInAnyUnitAndIsPackSelected(
      selectedOrganization,
      memberDetails.organizationPositions,
    );
    return isDenChief;
  },
);

export const groupedDenAdventuresSel = createSelector(
  getYouthCurrentDenRankId,
  adventuresSel,
  (denRankId, adventures) => {
    if (denRankId) {
      return groupBy(
        filter(adventures, adventure => adventure.rankId === Number(denRankId)),
        adventure =>
          adventure.isRequired || adventure.required ? 'required' : 'elective',
      );
    }
    return { required: [], elective: [] };
  },
);

export const advListCountsSel = createSelector(
  advancementsSel,
  payloadSel,
  (advancements, routeParams) => {
    const { advancementType } = routeParams;

    const typeAdvancements = advancements.filter(item => {
      if (item.advancementType !== advancementType) return false;
      if (item.status === advancementStatuses.DICTIONARY) return false;

      if (item.advancementType === advancementTypes.RANKS) {
        if (!isValueTruthy(item.active) && !item.awarded) return false;
      }

      return true;
    });

    const notStarted = typeAdvancements.filter(item => !item.status).length;
    const started = typeAdvancements.filter(
      item => item.status === advancementStatuses.STARTED,
    ).length;
    const completed = typeAdvancements.filter(
      item =>
        item.status === advancementStatuses.COMPLETED ||
        item.status === advancementStatuses.COUNSELOR_APPROVED,
    ).length;
    const approved = typeAdvancements.filter(
      item =>
        item.status === advancementStatuses.APPROVED ||
        item.status === advancementStatuses.AWARDED,
    ).length;

    return {
      [NewAdvancementFilter.NOT_STARTED]: notStarted,
      [NewAdvancementFilter.STARTED]: started,
      [NewAdvancementFilter.COMPLETED]: completed,
      [NewAdvancementFilter.APPROVED]: approved,
      [NewAdvancementFilter.APPROVED_COMPLETED]: completed + approved,
    };
  },
);

export const canSeeYouthInfoSectionSel = createSelector(
  organizationPositionSel,
  isParentOrYouthMemberSel,
  currentUserSubunitsSel,
  memberDetailsSel,
  state => hasNewPermissionSel(state, newPermissions.EDIT_PROFILE),
  state => hasNewPermissionSel(state, newPermissions.EDIT_SUB_PROFILE),
  (
    currentUnit,
    isParentOrYouthMember,
    currentUserSubUnits,
    youthDetails,
    canEditYouthProfile,
    canEditSubProfile,
  ) => {
    if (isParentOrYouthMember || canEditYouthProfile) return true;

    // Match to sub units
    if (canEditSubProfile) {
      if (!currentUserSubUnits.length || isEmpty(youthDetails)) return false;

      const foundYouthUnit = youthDetails.organizationPositions?.find(
        ({ organizationGuid }) =>
          organizationGuid === currentUnit.organizationGuid,
      );
      if (foundYouthUnit) {
        const hasValidSubUnits = foundYouthUnit.positions.some(pos => {
          const youthSubUnitId = pos.denId || pos.patrolId;
          return youthSubUnitId
            ? !!currentUserSubUnits.find(userSubunit => {
                const userSubunitId = userSubunit.denId || userSubunit.patrolId;

                return +userSubunitId === +youthSubUnitId;
              })
            : false;
        });

        return hasValidSubUnits;
      }
    }

    return false;
  },
);
